import { useRef, useState, useMemo, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  FormControl,
  FormLabel,
  Button,
  Spinner,
  Badge,
  Tooltip,
  SimpleGrid,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tabs,
  TabList,
  Tab,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@chakra-ui/react'
import {
  Search,
  LayoutList,
  CheckCircle2,
  Mail,
  Ticket,
  Inbox,
  Clock,
  BarChart2,
  CheckCheck,
  Timer,
  UserPlus,
  Trash2,
} from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAccessCheck } from '@/hooks/useAccess'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import type { MetaData } from '@/components/tables/types'
import {
  TimerCell,
  ComposeEmailModal,
  TicketRequestDetailModal,
} from '@/components/helpdesk'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAuthStore } from '@/store/auth'
import { isTeamLead } from '@/lib/utils/roleHelper'

/** Format date as "30 Apr 2025 - 09:30 AM" */
function formatSubmittedAt(value: string | null | undefined): string {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  const day = d.getDate()
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = months[d.getMonth()]
  const year = d.getFullYear()
  const hours = d.getHours()
  const minutes = d.getMinutes()
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const h12 = hours % 12 || 12
  const minStr = minutes < 10 ? `0${minutes}` : String(minutes)
  return `${day} ${month} ${year} - ${h12}:${minStr} ${ampm}`
}

// Base column config; filter options are merged from API in component.
const INCOMING_REQUEST_FIELDS: DataTableOptions['dataFields'] = {
  request_number: {
    name: 'Request ID',
    withSort: true,
    fullWidth: true,
  },
  client_id: {
    name: 'Client',
    withSort: true,
    withFilter: true,
    filterOptions: [],
    maxWidth: '160px',
  },
  service_type: {
    name: 'Request Type',
    withSort: true,
    withFilter: true,
    filterOptions: [],
    maxWidth: '130px',
  },
  email_subject: {
    name: 'Subject',
    withSort: false,
    maxWidth: '280px',
  },
  submitted_at: {
    name: 'Requested Date',
    withSort: true,
    maxWidth: '200px',
  },
  timer: {
    name: 'Timer',
    withSort: false,
    maxWidth: '110px',
  },
}

/** Assign to column — team leads get a dropdown; others see assigned user name only */
const ASSIGN_TO_FIELD: DataTableOptions['dataFields'] = {
  assign_to: {
    name: 'Assigned to',
    withSort: false,
    maxWidth: '220px',
  },
}

type FilterOption = { value: string | number; label: string }

type TabValue = 'all' | 'trash'

type PendingTrashAction = { kind: 'soft'; id: number }

function trashConfirmMessage(action: PendingTrashAction): string {
  switch (action.kind) {
    case 'soft':
      return 'Move this request to trash? You can restore it later from the Trash tab.'
  }
}

interface StatusBadgeProps {
  label: string
  count: number
  colorScheme: 'purple' | 'blue' | 'teal' | 'orange'
}

function StatusBadge({ label, count, colorScheme }: StatusBadgeProps) {
  const colorMap = {
    purple: { bg: 'purple.50', color: 'purple.700', border: 'purple.200' },
    blue: { bg: 'blue.50', color: 'blue.700', border: 'blue.200' },
    teal: { bg: 'teal.50', color: 'teal.700', border: 'teal.200' },
    orange: { bg: 'orange.50', color: 'orange.700', border: 'orange.200' },
  }
  const colors = colorMap[colorScheme]

  return (
    <Badge
      borderRadius="md"
      px={3}
      py={1.5}
      fontWeight="600"
      fontSize="sm"
      bg={colors.bg}
      color={colors.color}
      borderWidth="1px"
      borderColor={colors.border}
      variant="outline"
    >
      {label} : {count}
    </Badge>
  )
}

/** Incoming dashboard stats from GET /support/incoming/stats */
interface IncomingStats {
  total_open: number
  total_pending: number
  total_requests: number
  total_available_agents: number
  acknowledgement_rate: number
  on_time_ack_rate: number
  avg_ack_time_minutes: number
  volume_per_client: Array<{ client_name: string; count: number }>
  volume_per_transaction_type: Array<{ name: string; count: number }>
  volume_per_source: Array<{ source: string; count: number }>
}

function StatCard({
  label,
  value,
  colorScheme,
  icon,
}: {
  label: string
  value: string | number
  colorScheme: 'blue' | 'orange' | 'teal' | 'purple' | 'gray'
  icon?: React.ReactNode
}) {
  const colorMap = {
    blue: { bg: 'blue.50', color: 'blue.700', border: 'blue.200' },
    orange: { bg: 'orange.50', color: 'orange.700', border: 'orange.200' },
    teal: { bg: 'teal.50', color: 'teal.700', border: 'teal.200' },
    purple: { bg: 'purple.50', color: 'purple.700', border: 'purple.200' },
    gray: { bg: 'gray.50', color: 'gray.700', border: 'gray.200' },
  }
  const c = colorMap[colorScheme]
  return (
    <Card size="sm" borderWidth="1px" borderColor={c.border} bg={c.bg}>
      <CardBody py={3} px={4}>
        <HStack justify="space-between" align="flex-start">
          <Text fontSize="xs" fontWeight="600" color={c.color} textTransform="uppercase" letterSpacing="wider">
            {label}
          </Text>
          {icon && <Box color={c.color} opacity={0.7}>{icon}</Box>}
        </HStack>
        <Text fontSize="2xl" fontWeight="700" color={c.color} mt={1}>
          {value}
        </Text>
      </CardBody>
    </Card>
  )
}

const MEMBERS_API_PATH = '/access-control/users/members'

interface AvailableAgentRow {
  id?: number
  first_name?: string
  last_name?: string
  user_login?: string
  assigned_tickets_count?: number
  active_tickets_count?: number
}

export default function IncomingRequestPage() {
  const colors = useThemeColors()
  const { showSuccess, showError } = useToastNotification()
  const user = useAuthStore((s) => s.user)
  const userIsTeamLead = isTeamLead(user)
  const access = useAccessCheck()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const {
    isOpen: isTrashConfirmOpen,
    onOpen: onTrashConfirmOpen,
    onClose: onTrashConfirmCloseBase,
  } = useDisclosure()
  const [pendingTrashAction, setPendingTrashAction] = useState<PendingTrashAction | null>(null)
  const [isTrashConfirmProcessing, setIsTrashConfirmProcessing] = useState(false)

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [isTabSwitching, setIsTabSwitching] = useState(false)
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)

  const [searchQuery, setSearchQuery] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [clientOptions, setClientOptions] = useState<FilterOption[]>([])
  const [requestTypeOptions, setRequestTypeOptions] = useState<FilterOption[]>([])
  const [acknowledgingId, setAcknowledgingId] = useState<number | null>(null)
  const [stats, setStats] = useState<IncomingStats | null>(null)
  const [isLoadingStats, setIsLoadingStats] = useState(true)
  const [availableAgents, setAvailableAgents] = useState<AvailableAgentRow[]>([])
  const [isLoadingAgents, setIsLoadingAgents] = useState(false)
  const [composeRow, setComposeRow] = useState<Record<string, unknown> | null>(null)
  const [viewRow, setViewRow] = useState<Record<string, unknown> | null>(null)
  const [assignMemberList, setAssignMemberList] = useState<AvailableAgentRow[]>([])
  const [assigningId, setAssigningId] = useState<number | null>(null)

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [clientsRes, typesRes] = await Promise.all([
          apiClient.get('/options/clients'),
          apiClient.get('/options/service-types/request-types'),
        ])
        const clients = Array.isArray(clientsRes.data) ? clientsRes.data : []
        const types = Array.isArray(typesRes.data) ? typesRes.data : []
        setClientOptions(
          clients.map((c: { id: number; name?: string; code?: string }) => ({
            value: c.id,
            label: c.name || c.code || String(c.id),
          }))
        )
        setRequestTypeOptions(
          types.map((t: { id: number; name?: string; code?: string }) => ({
            value: t.id,
            label: t.name || t.code || String(t.id),
          }))
        )
      } catch {
        setClientOptions([])
        setRequestTypeOptions([])
      }
    }
    loadOptions()
  }, [])

  useEffect(() => {
    const fetchStats = async () => {
      setIsLoadingStats(true)
      try {
        // Stats should always reflect all incoming requests (no date filtering)
        const res = await apiClient.get('/support/incoming/stats')
        setStats(res.data ?? null)
      } catch {
        setStats(null)
      } finally {
        setIsLoadingStats(false)
      }
    }
    fetchStats()
  }, [dateFrom, dateTo])

  useEffect(() => {
    if (!userIsTeamLead) return
    const fetchAgents = async () => {
      setIsLoadingAgents(true)
      try {
        const res = await apiClient.get(MEMBERS_API_PATH, {
          params: { per_page: 10, order: 'assigned_tickets_count', sort: 'asc' },
        })
        const data = res.data?.data ?? res.data ?? []
        setAvailableAgents(Array.isArray(data) ? data : [])
      } catch {
        setAvailableAgents([])
      } finally {
        setIsLoadingAgents(false)
      }
    }
    fetchAgents()
  }, [userIsTeamLead])

  // Member list: resolve assigned user names for all users; team leads also use for Assign dropdown
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const res = await apiClient.get(MEMBERS_API_PATH, {
          params: { per_page: 100, order: 'first_name', sort: 'asc' },
        })
        const data = res.data?.data ?? res.data ?? []
        setAssignMemberList(Array.isArray(data) ? data : [])
      } catch {
        setAssignMemberList([])
      }
    }
    fetchMembers()
  }, [])

  const handleAcknowledge = useCallback(async (row: Record<string, unknown>) => {
    const id = row.id as number
    if (!id) return

    setAcknowledgingId(id)
    try {
      // Call the acknowledge endpoint
      await apiClient.post(`/support/my-request/${id}/acknowledge`)
      showSuccess('Success', 'Request acknowledged successfully')
      // Reload the table and refresh dashboard stats
      tableRef.current?.reload()
      try {
        // Re-fetch global stats (no filters) after acknowledge
        const res = await apiClient.get('/support/incoming/stats')
        setStats(res.data ?? null)
      } catch {
        // ignore stats refresh error
      }
      if (userIsTeamLead) {
        try {
          const agentsRes = await apiClient.get(MEMBERS_API_PATH, {
            params: { per_page: 10, order: 'assigned_tickets_count', sort: 'asc' },
          })
          const data = agentsRes.data?.data ?? agentsRes.data ?? []
          setAvailableAgents(Array.isArray(data) ? data : [])
        } catch {
          // ignore agents refresh error
        }
      }
    } catch (error: any) {
      showError(error)
    } finally {
      setAcknowledgingId(null)
    }
  }, [showSuccess, showError, dateFrom, dateTo, userIsTeamLead])

  const navigate = useNavigate()

  const handleCreateTicket = useCallback(
    (row: Record<string, unknown>) => {
      navigate('/tickets/create', {
        state: { fromRequest: row },
      })
    },
    [navigate]
  )

  const handleAssign = useCallback(
    async (row: Record<string, unknown>, userId: number | null) => {
      const id = row.id as number
      if (!id) return
      setAssigningId(id)
      try {
        await apiClient.patch(`/support/my-request/${id}/assign`, { assigned_to: userId })
        showSuccess('Success', userId ? 'Request assigned successfully' : 'Request unassigned')
        tableRef.current?.reload()
        try {
          const res = await apiClient.get('/support/incoming/stats')
          setStats(res.data ?? null)
        } catch {
          // ignore
        }
        if (userIsTeamLead) {
          try {
            const agentsRes = await apiClient.get(MEMBERS_API_PATH, {
              params: { per_page: 10, order: 'assigned_tickets_count', sort: 'asc' },
            })
            const data = agentsRes.data?.data ?? agentsRes.data ?? []
            setAvailableAgents(Array.isArray(data) ? data : [])
          } catch {
            // ignore
          }
        }
      } catch (error: any) {
        showError(error)
      } finally {
        setAssigningId(null)
      }
    },
    [showSuccess, showError, userIsTeamLead]
  )

  const refreshIncomingStats = useCallback(async () => {
    try {
      const res = await apiClient.get('/support/incoming/stats')
      setStats(res.data ?? null)
    } catch {
      // ignore
    }
  }, [])

  const onTrashConfirmClose = useCallback(() => {
    setPendingTrashAction(null)
    onTrashConfirmCloseBase()
  }, [onTrashConfirmCloseBase])

  const openTrashConfirm = useCallback(
    (action: PendingTrashAction) => {
      setPendingTrashAction(action)
      onTrashConfirmOpen()
    },
    [onTrashConfirmOpen]
  )

  const handleTabChange = useCallback((index: number) => {
    setIsTabSwitching(true)
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) searchRef.current.value = ''
    tableRef.current?.applyFilter('search', '')
    tableRef.current?.clearData()
    tableRef.current?.clearPage()
  }, [])

  const handleDataLoad = useCallback((_data: unknown[], meta?: MetaData) => {
    setIsTabSwitching(false)
    if (meta && typeof meta.all === 'number') setTotalRows(meta.all)
    if (meta && typeof meta.trashed === 'number') setTotalTrash(meta.trashed)
  }, [])

  const handleConfirmTrashAction = useCallback(async () => {
    if (!pendingTrashAction) return
    const { id } = pendingTrashAction
    setIsTrashConfirmProcessing(true)
    try {
      await apiClient.delete(`/support/my-request/${id}`)
      showSuccess('Success', 'Request moved to trash')
      setViewRow(null)
      tableRef.current?.reload()
      await refreshIncomingStats()
      onTrashConfirmClose()
    } catch (error: unknown) {
      showError(error as Error)
    } finally {
      setIsTrashConfirmProcessing(false)
    }
  }, [pendingTrashAction, showSuccess, showError, onTrashConfirmClose, refreshIncomingStats])

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      showActionsColumn: true,
      dataFields: {
        request_number: INCOMING_REQUEST_FIELDS.request_number,
        client_id: {
          ...INCOMING_REQUEST_FIELDS.client_id,
          filterOptions: clientOptions,
        },
        service_type: {
          ...INCOMING_REQUEST_FIELDS.service_type,
          filterOptions: requestTypeOptions,
        },
        email_subject: INCOMING_REQUEST_FIELDS.email_subject,
        submitted_at: INCOMING_REQUEST_FIELDS.submitted_at,
        ...ASSIGN_TO_FIELD,
        ...(activeTab === 'all' ? { timer: INCOMING_REQUEST_FIELDS.timer } : {}),
      },
      dataSource: activeTab === 'trash' ? '/support/my-request/archived' : '/support/my-request',
      primaryKey: 'id',
      softDelete: activeTab === 'trash',
      displayInModal: false,
      noRecordText: 'No Record Found',
      hideSelection: true,
      otherActions: [
        {
          name: 'Compose Email',
          onClick: (row: Record<string, unknown>) => {
            setComposeRow(row)
          },
          show: () => true,
          render: (_row: Record<string, unknown>) => (
            <Tooltip label="Compose Email" placement="top" hasArrow>
              <IconButton
                aria-label="Compose Email"
                icon={<Mail size={18} />}
                variant="ghost"
                size="sm"
                colorScheme="gray"
                onClick={(e) => {
                  e.stopPropagation()
                  setComposeRow(_row)
                }}
              />
            </Tooltip>
          ),
        },
        {
          name: 'Acknowledge',
          onClick: handleAcknowledge,
          show: (row: Record<string, unknown>) => !row.acknowledged_at, // Only show if not already acknowledged
          render: (row: Record<string, unknown>, _isLoading?: boolean, onClick?: () => void) => {
            const isAcknowledging = acknowledgingId === (row.id as number)
            return (
              <Button
                size="sm"
                colorScheme="blue"
                isLoading={isAcknowledging}
                isDisabled={isAcknowledging}
                onClick={(e) => {
                  e.stopPropagation()
                  onClick?.()
                }}
              >
                Acknowledge
              </Button>
            )
          },
        },
        {
          name: 'Create Ticket',
          onClick: handleCreateTicket,
          show: (row: Record<string, unknown>) => !!row.acknowledged_at, // Only show if already acknowledged
          render: (_row: Record<string, unknown>) => (
            <Tooltip label="Create Ticket" placement="top" hasArrow>
              <IconButton
                aria-label="Create Ticket"
                icon={<Ticket size={18} />}
                variant="ghost"
                size="sm"
                colorScheme="green"
                onClick={(e) => {
                  e.stopPropagation()
                  handleCreateTicket(_row)
                }}
              />
            </Tooltip>
          ),
        },
      ],
    }),
    [
      activeTab,
      clientOptions,
      requestTypeOptions,
      handleAcknowledge,
      handleCreateTicket,
      handleAssign,
      assignMemberList,
      acknowledgingId,
      assigningId,
    ]
  )

  const permissions = useMemo(
    () => ({
      can_edit: false,
      can_delete: access.can_delete,
      can_create: false,
      can_view: true,
      can_export: false,
      can_import: false,
    }),
    [access.can_delete]
  )

  const tableParams = useMemo(
    () => ({
      search: searchQuery,
      incoming: 1,
      ...(dateFrom && { date_from: dateFrom }),
      ...(dateTo && { date_to: dateTo }),
    }),
    [searchQuery, dateFrom, dateTo]
  )

  const customRenderers = useMemo(
    () => ({
      client_id: (_value: unknown, row?: Record<string, unknown>) => {
        const name = (row?.client_name ?? row?.contact_name) as string | null | undefined
        return <Text as="span">{name ?? '–'}</Text>
      },
      request_number: (value: unknown) => {
        const str = value ? String(value) : '–'
        return <Text as="span" whiteSpace="nowrap">{str}</Text>
      },
      request_source: (value: string | null | undefined) => {
        if (!value) return <Text as="span" color="gray.500">–</Text>
        // Format: capitalize first letter and replace underscores with spaces
        const formatted = value
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
        return <Text as="span">{formatted}</Text>
      },
      email_subject: (value: unknown, row?: Record<string, unknown>) => {
        const subject = (value ?? row?.description) as string | null | undefined
        const text = subject || '–'
        if (!row) {
          return <Text as="span">{text}</Text>
        }
        return (
          <Text
            as="button"
            type="button"
            variant="link"
            color={colors.brandBlue}
            fontWeight="500"
            textAlign="left"
            textDecoration="underline"
            cursor="pointer"
            whiteSpace="normal"
            noOfLines={3}
            title={text !== '–' ? text : undefined}
            onClick={(e) => {
              e.stopPropagation()
              setViewRow(row)
            }}
          >
            {text}
          </Text>
        )
      },
      submitted_at: (value: string | null | undefined) => formatSubmittedAt(value),
      timer: (value: unknown, row?: Record<string, unknown>) => {
        const submittedAt = (row?.submitted_at ?? value) as string | null | undefined
        if (!submittedAt || typeof submittedAt !== 'string') return <Text as="span" color="gray.500">–</Text>
        const acknowledgedAt = row?.acknowledged_at as string | null | undefined
        const delayMinutes = (row?.acknowledge_delay_minutes as number | null | undefined) ?? 0
        if (acknowledgedAt) {
          const label = delayMinutes > 0
            ? `Acknowledged at ${formatSubmittedAt(acknowledgedAt)} (${delayMinutes} min late)`
            : `Acknowledged at ${formatSubmittedAt(acknowledgedAt)} (on time)`
          return (
            <Text as="span" color={delayMinutes > 0 ? 'orange.600' : 'green.600'} title={label}>
              {label}
            </Text>
          )
        }
        return <TimerCell requestedAt={submittedAt} />
      },
      assign_to: (_value: unknown, row?: Record<string, unknown>) => {
        if (!row || row.parent_ticket_id) return <Text as="span" color="gray.500">–</Text>
        const assignedToId = row.assigned_to as number | null | undefined
        const assignedToName = row.assigned_to_name as string | null | undefined
        const member = assignMemberList.find((m) => m.id === assignedToId)
        const assignedName =
          (assignedToName && assignedToName.trim()) ||
          (member
            ? [member.first_name, member.last_name].filter(Boolean).join(' ') || member.user_login || `User #${member.id}`
            : assignedToId != null
              ? `User #${assignedToId}`
              : null)
        const isAssigning = assigningId === (row.id as number)

        if (!userIsTeamLead) {
          return (
            <Text fontSize="sm" noOfLines={2} title={assignedName ?? undefined} color={assignedName ? 'inherit' : 'gray.500'}>
              {assignedName ?? '–'}
            </Text>
          )
        }

        return (
          <HStack w="full" justify="space-between" spacing={2} onClick={(e) => e.stopPropagation()}>
            {assignedName ? (
              <Text fontSize="sm" noOfLines={1} title={assignedName} flex="1" minW={0} textAlign="left">
                {assignedName}
              </Text>
            ) : (
              <Box flex="1" minW={0} />
            )}
            <Menu placement="bottom-end" strategy="fixed">
              <Tooltip label="Assign to" placement="top" hasArrow>
                <MenuButton
                  as={IconButton}
                  aria-label="Assign to"
                  icon={<UserPlus size={18} />}
                  variant="ghost"
                  size="sm"
                  colorScheme="gray"
                  isLoading={isAssigning}
                  isDisabled={isAssigning}
                  onClick={(e) => e.stopPropagation()}
                />
              </Tooltip>
              <MenuList maxH="300px" overflowY="auto" minW="220px" onClick={(e) => e.stopPropagation()}>
                <MenuItem onClick={() => handleAssign(row, null)}>
                  <Text fontSize="sm">Unassign</Text>
                </MenuItem>
                {assignMemberList.map((m) => {
                  const name = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.user_login || `User #${m.id}`
                  return (
                    <MenuItem
                      key={m.id}
                      onClick={() => handleAssign(row, m.id!)}
                      bg={row.assigned_to === m.id ? 'blue.50' : 'transparent'}
                    >
                      <Text fontSize="sm" noOfLines={1}>{name}</Text>
                    </MenuItem>
                  )
                })}
              </MenuList>
            </Menu>
          </HStack>
        )
      },
    }),
    [assignMemberList, handleAssign, assigningId, userIsTeamLead, colors.brandBlue]
  )

  const handleSearch = () => {
    const query = searchRef.current?.value ?? ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <Box p={6} w="100%" minW={0} overflow="hidden" display="flex" flexDirection="column">
      <VStack spacing={2} align="stretch" flex={1} overflow="hidden" minH={0}>
        <Card flexShrink={0} display="flex" flexDirection="column" flex={1} minH={0} overflow="hidden">


          <CardBody
            p={4}
            flex="1"
            overflow="hidden"
            display="flex"
            flexDirection="column"
            minH={0}
            position="relative"
          >
            <Box
              flex="1"
              display="flex"
              flexDirection={{ base: 'column', lg: 'row' }}
              gap={4}
              alignItems="stretch"
              minH={0}
            >
              {/* Left column – Stats & volume analytics */}
              <Box
                flex="1"
                minH={0}
                display="flex"
                flexDirection="column"
                gap={3}
              >
                {/* Row 1 – Operational Cards */}
                <Box flexShrink={0}>
                  {isLoadingStats ? (
                    <HStack spacing={3}>
                      <Spinner size="sm" />
                      <Text fontSize="sm" color="gray.500">
                        Loading stats…
                      </Text>
                    </HStack>
                  ) : stats ? (
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
                      <StatCard label="Total Open" value={stats.total_open} colorScheme="blue" icon={<Inbox size={16} />} />
                      <StatCard label="Total Pending" value={stats.total_pending} colorScheme="orange" icon={<Clock size={16} />} />
                      <StatCard label="Total Requests" value={stats.total_requests ?? 0} colorScheme="teal" icon={<BarChart2 size={16} />} />
                    </SimpleGrid>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      Unable to load stats
                    </Text>
                  )}
                </Box>

                {/* Row 2 – SLA KPI Cards */}
                {stats && (
                  <Box flexShrink={0}>
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
                      <StatCard
                        label="Acknowledgement Rate"
                        value={`${stats.acknowledgement_rate}%`}
                        colorScheme="purple"
                        icon={<CheckCheck size={16} />}
                      />
                      <StatCard
                        label="On-Time Ack Rate"
                        value={`${stats.on_time_ack_rate}%`}
                        colorScheme="purple"
                        icon={<CheckCircle2 size={16} />}
                      />
                      <StatCard
                        label="Avg Ack Time"
                        value={`${stats.avg_ack_time_minutes} min`}
                        colorScheme="gray"
                        icon={<Timer size={16} />}
                      />
                    </SimpleGrid>
                  </Box>
                )}

                {/* Row 3 – Volume Analytics */}
                {stats && (
                  <Box flexShrink={0}>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                      <Card size="sm" borderWidth="1px" borderColor="gray.200">
                        <CardHeader py={2} px={4}>
                          <Heading size="xs" textTransform="uppercase" letterSpacing="wider">
                            Per Client
                          </Heading>
                        </CardHeader>
                        <CardBody pt={0} px={4} pb={3}>
                          <Box maxH="280px" overflowY="auto" pr={2}>
                            <VStack align="stretch" spacing={1}>
                              {stats.volume_per_client.length === 0 ? (
                                <Text fontSize="sm" color="gray.500">
                                  No data
                                </Text>
                              ) : (
                                stats.volume_per_client.map(({ client_name, count }) => (
                                  <HStack key={client_name} justify="space-between" flexShrink={0}>
                                    <Text fontSize="sm" noOfLines={1}>
                                      {client_name}
                                    </Text>
                                    <Text fontSize="sm" fontWeight="600">
                                      {count}
                                    </Text>
                                  </HStack>
                                ))
                              )}
                            </VStack>
                          </Box>
                        </CardBody>
                      </Card>
                      <Card size="sm" borderWidth="1px" borderColor="gray.200">
                        <CardHeader py={2} px={4}>
                          <Heading size="xs" textTransform="uppercase" letterSpacing="wider">
                            Per Transaction Type
                          </Heading>
                        </CardHeader>
                        <CardBody pt={0} px={4} pb={3}>
                          <VStack align="stretch" spacing={1}>
                            {stats.volume_per_transaction_type.length === 0 ? (
                              <Text fontSize="sm" color="gray.500">
                                No data
                              </Text>
                            ) : (
                              stats.volume_per_transaction_type.map(({ name, count }) => (
                                <HStack key={name} justify="space-between">
                                  <Text fontSize="sm" noOfLines={1}>
                                    {name}
                                  </Text>
                                  <Text fontSize="sm" fontWeight="600">
                                    {count}
                                  </Text>
                                </HStack>
                              ))
                            )}
                          </VStack>
                        </CardBody>
                      </Card>
                      <Card size="sm" borderWidth="1px" borderColor="gray.200">
                        <CardHeader py={2} px={4}>
                          <Heading size="xs" textTransform="uppercase" letterSpacing="wider">
                            Per Source
                          </Heading>
                        </CardHeader>
                        <CardBody pt={0} px={4} pb={3}>
                          <VStack align="stretch" spacing={1}>
                            {stats.volume_per_source.length === 0 ? (
                              <Text fontSize="sm" color="gray.500">
                                No data
                              </Text>
                            ) : (
                              stats.volume_per_source.map(({ source, count }) => (
                                <HStack key={source} justify="space-between">
                                  <Text fontSize="sm" textTransform="capitalize">
                                    {source}
                                  </Text>
                                  <Text fontSize="sm" fontWeight="600">
                                    {count}
                                  </Text>
                                </HStack>
                              ))
                            )}
                          </VStack>
                        </CardBody>
                      </Card>
                    </SimpleGrid>
                  </Box>
                )}
              </Box>

              {/* Right column – Available Agents (TL/STL only) */}
              {userIsTeamLead && (
                <Box flex={{ base: '0 0 auto', lg: '0 0 320px' }} maxW={{ base: '100%', lg: '340px' }}>
                  <Card size="sm" borderWidth="1px" borderColor="gray.200" h="100%">
                    <CardHeader py={2} px={4}>
                      <HStack justify="space-between" align="center">
                        <Box>
                          <Heading size="sm">Available Agents</Heading>
                          <Text fontSize="xs" color="gray.500">
                            10 agents with fewest assigned tickets
                          </Text>
                        </Box>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="blue"
                          onClick={() => navigate('/agent-status')}
                        >
                          View All
                        </Button>
                      </HStack>
                    </CardHeader>
                    <CardBody pt={0} px={4} pb={4} display="flex" flexDirection="column" minH={0}>
                      {isLoadingAgents ? (
                        <HStack spacing={2} py={4}>
                          <Spinner size="sm" />
                          <Text fontSize="sm" color="gray.500">Loading agents…</Text>
                        </HStack>
                      ) : availableAgents.length === 0 ? (
                        <Text fontSize="sm" color="gray.500" py={4}>No agents found.</Text>
                      ) : (
                        <VStack align="stretch" spacing={2}>
                          <SimpleGrid columns={3} spacing={2} fontSize="xs" color="gray.500" pb={1} borderBottom="1px" borderColor="gray.200" w="100%" templateColumns="1fr auto auto">
                            <Text fontWeight="600">Name</Text>
                            <Text fontWeight="600" textAlign="center" pl={4}>Total</Text>
                            <Text fontWeight="600" textAlign="center" pl={2}>Active</Text>
                          </SimpleGrid>
                          {availableAgents.map((agent, index) => {
                            const name = [agent.first_name, agent.last_name].filter(Boolean).join(' ') || agent.user_login || '–'
                            const total = agent.assigned_tickets_count ?? 0
                            const active = agent.active_tickets_count ?? 0
                            return (
                              <SimpleGrid key={agent.id ?? index} columns={3} spacing={2} w="100%" alignItems="center" templateColumns="1fr auto auto">
                                <Tooltip label={name} placement="top" hasArrow>
                                  <Text fontSize="sm" noOfLines={2} title={name}>{name}</Text>
                                </Tooltip>
                                <Box pl={4}>
                                  <Text fontSize="sm" textAlign="center"><Badge size="sm" colorScheme="blue" variant="subtle">{total}</Badge></Text>
                                </Box>
                                <Box pl={2}>
                                  <Text fontSize="sm" textAlign="center"><Badge size="sm" colorScheme="teal" variant="subtle">{active}</Badge></Text>
                                </Box>
                              </SimpleGrid>
                            )
                          })}
                        </VStack>
                      )}
                    </CardBody>
                  </Card>
                </Box>
              )}
            </Box>

            <Box flexShrink={0} mt={6} mb={4}>
              <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange} variant="enclosed">
                <TabList borderBottom="none">
                  <Tab>
                    <HStack spacing={2}>
                      <LayoutList size={14} />
                      <Text>All</Text>
                      <Badge colorScheme="blue">{totalRows}</Badge>
                    </HStack>
                  </Tab>
                  <Tab>
                    <HStack spacing={2}>
                      <Trash2 size={14} />
                      <Text>Trash</Text>
                      <Badge colorScheme="red">{totalTrash}</Badge>
                    </HStack>
                  </Tab>
                </TabList>
              </Tabs>
              <Box borderBottom="2px" borderColor={colors.border} />
            </Box>
            <Box flexShrink={0} mb={4}>
              <HStack justify="flex-start" align="flex-end" spacing={4} width="100%" flexWrap="wrap">
                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search by request #, contact, description..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
                <HStack align="flex-end" spacing={2}>
                  <FormControl width="auto">
                    <FormLabel fontSize="xs" mb={1} whiteSpace="nowrap">
                      From
                    </FormLabel>
                    <Input
                      type="date"
                      size="sm"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      max={dateTo || undefined}
                    />
                  </FormControl>
                  <FormControl width="auto">
                    <FormLabel fontSize="xs" mb={1} whiteSpace="nowrap">
                      To
                    </FormLabel>
                    <Input
                      type="date"
                      size="sm"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      min={dateFrom || undefined}
                    />
                  </FormControl>
                </HStack>
              </HStack>
            </Box>
            <Box
              flex="1"
              overflow={activeTab === 'trash' ? 'visible' : 'hidden'}
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <DataTable
                ref={tableRef}
                options={tableOptions}
                params={tableParams}
                access={permissions}
                filterOptions={{
                  client_id: clientOptions,
                  service_type: requestTypeOptions,
                }}
                customRenderers={customRenderers}
                onDataLoad={handleDataLoad}
                actionLoadingStates={{
                  ...(acknowledgingId !== null && { [acknowledgingId]: true }),
                  ...(assigningId !== null && { [assigningId]: true }),
                }}
              />
              {isTabSwitching ? (
                <VStack
                  position="absolute"
                  inset={0}
                  justify="center"
                  align="center"
                  spacing={3}
                  bg="whiteAlpha.800"
                  _dark={{ bg: 'blackAlpha.500' }}
                  zIndex={2}
                >
                  <Spinner size="md" />
                  <Text fontSize="sm" color="gray.500">Loading requests...</Text>
                </VStack>
              ) : null}
            </Box>
          </CardBody>
        </Card>
      </VStack>

      <Modal
        isOpen={isTrashConfirmOpen}
        onClose={isTrashConfirmProcessing ? () => {} : onTrashConfirmClose}
        isCentered
        closeOnOverlayClick={!isTrashConfirmProcessing}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm</ModalHeader>
          <ModalBody>
            {pendingTrashAction ? trashConfirmMessage(pendingTrashAction) : ''}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onTrashConfirmClose} isDisabled={isTrashConfirmProcessing}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirmTrashAction}
              isLoading={isTrashConfirmProcessing}
            >
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ComposeEmailModal
        isOpen={composeRow != null}
        onClose={() => setComposeRow(null)}
        row={composeRow}
      />

      <TicketRequestDetailModal
        isOpen={viewRow != null}
        onClose={() => setViewRow(null)}
        row={viewRow}
        showDelete={activeTab === 'all' && access.can_delete}
        onDelete={() => {
          const id = viewRow?.id as number | undefined
          if (!id) return
          openTrashConfirm({ kind: 'soft', id })
        }}
        isDeleting={isTrashConfirmProcessing && pendingTrashAction?.kind === 'soft'}
      />
    </Box>
  )
}
